import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  ArrowDown,
  Cog,
  Settings,
  Cpu,
  Wrench,
  Factory,
  ShieldCheck,
  Zap,
  Construction,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { SITE, IMAGES } from "@/config/site";
import Section, { RevealItem } from "@/components/Section";
import FloatingObjects from "@/components/FloatingObjects";
import PageHead from "@/components/PageHead";

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const { t, lang } = useLang();
  const heroRef = useRef(null);
  const heroBgRef = useRef(null);
  const heroContentRef = useRef(null);
  const craneRef = useRef(null);
  const beamRef = useRef(null);
  const gearBigRef = useRef(null);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const ctx = gsap.context(() => {
      // Hero camera-zoom: scale background up, fade text down as user scrolls
      gsap.to(heroBgRef.current, {
        scale: isMobile ? 1.15 : 1.35,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 0.6,
        },
      });
      gsap.to(heroContentRef.current, {
        y: isMobile ? -40 : -80,
        opacity: 0,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom 30%",
          scrub: 0.6,
        },
      });
      // Floating elements depth
      if (craneRef.current) {
        gsap.to(craneRef.current, {
          y: isMobile ? -30 : -90,
          rotate: -2,
          ease: "none",
          scrollTrigger: { trigger: heroRef.current, start: "top top", end: "bottom top", scrub: 1 },
        });
      }
      if (beamRef.current) {
        gsap.to(beamRef.current, {
          x: isMobile ? 30 : 90,
          y: isMobile ? 20 : 40,
          ease: "none",
          scrollTrigger: { trigger: heroRef.current, start: "top top", end: "bottom top", scrub: 1 },
        });
      }
      if (gearBigRef.current) {
        gsap.to(gearBigRef.current, {
          rotate: 360,
          ease: "none",
          scrollTrigger: { trigger: heroRef.current, start: "top top", end: "bottom top", scrub: 1.2 },
        });
      }

      // Section storytelling parallax
      gsap.utils.toArray(".story-bg").forEach((el) => {
        gsap.to(el, {
          y: isMobile ? -40 : -120,
          ease: "none",
          scrollTrigger: { trigger: el.parentElement, start: "top bottom", end: "bottom top", scrub: 1 },
        });
      });

      // Stat counters
      gsap.utils.toArray("[data-counter]").forEach((el) => {
        const end = parseInt(el.dataset.counter, 10);
        gsap.fromTo(
          el,
          { innerText: 0 },
          {
            innerText: end,
            duration: 1.6,
            ease: "power2.out",
            snap: { innerText: 1 },
            scrollTrigger: { trigger: el, start: "top 85%" },
            onUpdate() {
              el.innerText = Math.floor(this.targets()[0].innerText).toString();
            },
          }
        );
      });
    });
    return () => ctx.revert();
  }, [lang]);

  return (
    <>
      <PageHead customTitle={`${SITE.brand.en} · ${t.hero.title}`} customDesc={t.hero.subtitle} />

      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative min-h-[100svh] overflow-hidden flex items-center"
      >
        <div ref={heroBgRef} className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/80 via-navy-800/65 to-navy-900" />
          <div className="absolute inset-0 grid-blueprint opacity-30" />
        </div>

        {/* Floating depth objects */}
        <div ref={craneRef} className="absolute -top-10 right-[6%] hidden md:block opacity-20">
          <Construction className="w-72 h-72 text-metallic" strokeWidth={0.7} />
        </div>
        <div ref={beamRef} className="absolute bottom-16 left-[4%] hidden md:block opacity-15">
          <div className="w-72 h-2 bg-metallic-light skew-y-3 shadow-[0_0_30px_rgba(212,175,55,0.25)]" />
          <div className="w-56 h-2 bg-metallic-light skew-y-3 mt-3 ms-8" />
        </div>
        <div ref={gearBigRef} className="absolute -bottom-24 -right-24 opacity-10">
          <Cog className="w-[420px] h-[420px] text-gold-500" strokeWidth={0.5} />
        </div>

        <div ref={heroContentRef} className="relative container-pad py-32 md:py-40 z-10 w-full">
          <div className="max-w-3xl">
            <div className="text-overline mb-6" data-testid="hero-overline">
              {t.hero.overline}
            </div>
            <h1
              data-testid="hero-title"
              className="font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05]"
            >
              {t.hero.title}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-6 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed"
            >
              {t.hero.subtitle}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/services" className="btn-primary group" data-testid="hero-cta-services">
                <span>{t.hero.ctaPrimary}</span>
                <ArrowRight className="w-4 h-4 rtl-flip group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" className="btn-secondary" data-testid="hero-cta-contact">
                {t.hero.ctaSecondary}
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-3 text-white/60 text-xs tracking-[0.25em] uppercase">
              <span>{t.hero.scroll}</span>
              <ArrowDown className="w-4 h-4 animate-float" />
            </div>
          </div>
        </div>

        {/* Spark cluster */}
        <div className="absolute bottom-10 right-10 hidden lg:flex gap-2" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="block w-1 h-1 bg-gold-500 rounded-full animate-spark"
              style={{ animationDelay: `${i * 0.18}s` }}
            />
          ))}
        </div>
      </section>

      {/* SECTION 1 — Built for Industrial Excellence */}
      <Section testId="home-section-excellence" className="overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <FloatingObjects density="low" />
        </div>
        <div className="container-pad grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <RevealItem className="text-overline mb-4">{t.homeSections.s1Overline}</RevealItem>
            <RevealItem
              as="h2"
              className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-5xl text-white leading-tight"
            >
              {t.homeSections.s1Title}
            </RevealItem>
            <RevealItem className="mt-5 text-white/70 leading-relaxed">
              {t.homeSections.s1Body}
            </RevealItem>

            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              {[
                { v: 15, k: t.homeSections.stats.years, sfx: "+" },
                { v: 12, k: t.homeSections.stats.regions, sfx: "" },
                { v: 66, k: t.homeSections.stats.manpower, sfx: "+" },
                { v: 24, k: t.homeSections.stats.services, sfx: "+" },
              ].map((s, i) => (
                <RevealItem key={i} className="border-t border-white/10 pt-3">
                  <div className="font-heading font-black text-3xl md:text-4xl text-gold-shine">
                    <span data-counter={s.v}>0</span>
                    {s.sfx}
                  </div>
                  <div className="text-xs text-white/55 mt-1 tracking-wider">{s.k}</div>
                </RevealItem>
              ))}
            </div>
          </div>

          <RevealItem className="lg:col-span-7 relative">
            <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
              <img
                src={IMAGES.about}
                alt="Industrial steel frame"
                className="story-bg w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-navy-900/85 via-navy-800/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="text-overline">EAH · Industrial</div>
                <div className="font-heading font-bold text-2xl md:text-3xl text-white mt-2">
                  {lang === "ar" ? "هندسة محفورة في كل وصلة" : "Engineering forged into every joint"}
                </div>
              </div>
              {/* Metallic strip top */}
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-500 to-transparent" />
            </div>
          </RevealItem>
        </div>
      </Section>

      {/* SECTION 2 — From Fabrication to Automation */}
      <Section testId="home-section-fab-to-auto" className="bg-navy-900/60 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={IMAGES.fabrication}
            alt=""
            className="w-full h-full object-cover opacity-20"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/90 to-navy-900/80" />
        </div>
        <div className="container-pad grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <RevealItem className="text-overline mb-4">{t.homeSections.s2Overline}</RevealItem>
            <RevealItem
              as="h2"
              className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-5xl text-white leading-tight"
            >
              {t.homeSections.s2Title}
            </RevealItem>
            <RevealItem className="mt-5 text-white/70 leading-relaxed">
              {t.homeSections.s2Body}
            </RevealItem>
            <div className="mt-8 space-y-4">
              {[
                { n: "01", title: t.services.cards[1].t, body: t.services.cards[1].d },
                { n: "02", title: t.services.cards[2].t, body: t.services.cards[2].d },
              ].map((item) => (
                <RevealItem key={item.n} className="flex gap-4 border-l border-gold-500/40 ps-4">
                  <div className="font-mono text-gold-500 text-sm pt-1">{item.n}</div>
                  <div>
                    <div className="font-heading font-bold text-white leading-snug">{item.title}</div>
                    <p className="mt-1 text-sm text-white/58 leading-relaxed line-clamp-2">{item.body}</p>
                  </div>
                </RevealItem>
              ))}
            </div>
          </div>

          <RevealItem className="lg:col-span-7">
            <div className="relative min-h-[520px] border border-white/10 bg-navy-900/70 overflow-hidden">
              <img
                src={IMAGES.fabrication}
                alt="Steel fabrication workshop"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-navy-900/10" />
              <div className="absolute inset-0 grid-blueprint opacity-20" />
              <div className="relative h-full min-h-[520px] p-5 md:p-7 flex flex-col justify-end">
                <div className="grid sm:grid-cols-2 gap-4">
            {[
              { Icon: Zap, label: t.services.cards[0].t, body: t.services.cards[0].d, key: "elec" },
              { Icon: Factory, label: t.services.cards[1].t, body: t.services.cards[1].d, key: "fab" },
              { Icon: Cpu, label: t.services.cards[2].t, body: t.services.cards[2].d, key: "auto" },
              { Icon: Wrench, label: t.services.cards[3].t, body: t.services.cards[3].d, key: "maint" },
            ].map((s) => (
              <RevealItem
                key={s.key}
                className="metallic-border-top group relative p-5 bg-navy-900/82 hover:bg-navy-800/92 border border-white/10 transition-all duration-300 hover:-translate-y-1 hover:border-gold-500/45"
              >
                <div className="w-14 h-14 border border-white/10 flex items-center justify-center bg-navy-900 group-hover:border-gold-500/50 transition-colors">
                  <s.Icon className="w-6 h-6 text-gold-500" strokeWidth={1.5} />
                </div>
                <div className="mt-5 font-heading font-bold text-white leading-snug">{s.label}</div>
                <p className="mt-3 text-sm text-white/58 leading-relaxed line-clamp-3">{s.body}</p>
                <div className="mt-3 text-xs text-white/50 tracking-wider uppercase">
                  {lang === "ar" ? "اعرف المزيد" : "Learn more"}
                </div>
                <Link to="/services" className="absolute inset-0" aria-label={s.label} />
              </RevealItem>
            ))}
                </div>
              </div>
            </div>
          </RevealItem>
        </div>
      </Section>

      {/* SECTION 3 — Trusted Partner */}
      <Section testId="home-section-trusted" className="overflow-hidden">
        <div className="container-pad">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <RevealItem className="text-overline mb-3">{t.homeSections.s3Overline}</RevealItem>
              <RevealItem
                as="h2"
                className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-5xl text-white leading-tight max-w-2xl"
              >
                {t.homeSections.s3Title}
              </RevealItem>
              <RevealItem className="mt-4 text-white/70 max-w-xl">
                {t.homeSections.s3Body}
              </RevealItem>
            </div>
            <RevealItem>
              <Link to="/clients" className="btn-secondary">
                {lang === "ar" ? "عرض كل العملاء" : "View all clients"}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </Link>
            </RevealItem>
          </div>

          {/* Client marquee */}
          <div className="marquee-mask overflow-hidden border-y border-white/5 py-6">
            <div className="flex gap-12 whitespace-nowrap animate-marquee" style={{ width: "max-content" }}>
              {[...t.clients.list, ...t.clients.list].map((name, idx) => (
                <span
                  key={idx}
                  className="text-metallic font-heading font-extrabold text-2xl md:text-3xl tracking-wider px-6"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom stripe */}
        <div className="container-pad mt-16">
          <div className="glass-strong p-8 md:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 border border-gold-500/40 bg-navy-900 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-gold-500" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-overline">EAH · CTA</div>
                <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mt-1 leading-tight">
                  {lang === "ar"
                    ? "هل لديك مشروع صناعي؟ تحدث مع مهندسينا."
                    : "Have an industrial project? Talk to our engineers."}
                </h3>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="home-bottom-cta-contact">
                {t.hero.ctaSecondary}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </Link>
              <a
                href={`tel:${SITE.contact.phones[0].replace(/\s/g, "")}`}
                className="btn-secondary"
                data-testid="home-bottom-cta-call"
              >
                {t.contact.callNow}
              </a>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
